import { useMemo, useState } from "react";

import clsx from "clsx";
import { compact, groupBy, sortBy } from "lodash";

import { Modal } from "@components/shared/modal";
import {
  Accordion,
  Badge,
  ButtonGroup,
  Checkbox,
  ModalPanel,
  PrimaryButton,
  SearchInput,
  TertiaryButton,
} from "@components/v2";
import { usePrettyLogEventPage } from "@components/v2/analytics";
import { useTableDensity, type TableDensity } from "@components/v2/table/table";
import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import { useSalesV4Context } from "../../hooks";

import { MetricItem } from "./metric-item";

const GROUP_LABEL: Record<string, { label: string; platformName: string }> = {
  northbeam: { label: "Northbeam", platformName: "Northbeam" },
  meta: { label: "Meta Reported", platformName: "Facebook Ads" },
  tiktok: { label: "TikTok Reported", platformName: "TikTok" },
  google: { label: "Google Reported", platformName: "Google Ads" },
  snapchat: { label: "Snapchat Reported", platformName: "Snapchat Ads" },
  pinterest: { label: "Pinterest Reported", platformName: "Pinterest" },
  twitter: { label: "Twitter Reported", platformName: "Twitter" },
  tradedesk: { label: "Trade Desk Reported", platformName: "Tradedesk" },
};

interface Props {
  isOpen: boolean;
  selectedMetricIds: string[];
  onChange: (metricIds: string[]) => void;
  onClose: () => void;
}

export const CustomizeTableModal = ({
  isOpen,
  selectedMetricIds,
  onChange,
  onClose,
}: Props) => {
  return (
    <Modal
      variant="v2"
      contentClassName="max-w-[920px] h-[80%] flex flex-col"
      bodyClassName="flex flex-col overflow-hidden"
      isOpen={isOpen}
      onClose={onClose}
    >
      <Content
        onClose={onClose}
        selectedMetricIds={selectedMetricIds}
        onChange={onChange}
      />
    </Modal>
  );
};

export const Content = ({
  selectedMetricIds,
  onChange,
  onClose,
}: Omit<Props, "isOpen">) => {
  const { dimensions, metrics } = useSalesV4Context();
  const prettyLogEventPage = usePrettyLogEventPage();

  const [search, setSearch] = useState("");

  const [selectedColumns, setSelectedColumns] = useState<
    {
      id: string;
      label: string;
    }[]
  >(() => {
    const cols = [];

    cols.push(
      ...compact(
        selectedMetricIds.map((id) =>
          dimensions.find((d) => `dimension:${d.id}` === id),
        ),
      ).map((e) => ({ id: `dimension:${e.id}`, label: e.name })),
    );

    cols.push(
      ...compact(
        selectedMetricIds.map((id) => metrics.find((m) => m.id === id)),
      ).map((e) => ({ id: e.id, label: e.name })),
    );

    return cols;
  });

  const filteredMetrics = useMemo(() => {
    if (!search) {
      return metrics;
    }
    const searchLower = search.toLowerCase();
    return metrics.filter((metric) =>
      metric.name.toLowerCase().includes(searchLower),
    );
  }, [metrics, search]);

  // Force accordion re-render when search changes
  const accordionKey = useMemo(() => search, [search]);

  const groupedMetrics = useMemo(() => {
    const groups = groupBy(filteredMetrics, "category");

    const result = [];

    for (const [key, items] of Object.entries(groups)) {
      const group = GROUP_LABEL[key];

      if (group) {
        result.push({ group: { id: key, ...group }, items });
      }
    }

    return result;
  }, [filteredMetrics]);

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    const oldIndex = selectedColumns.findIndex((item) => item.id === active.id);
    const newIndex = selectedColumns.findIndex((item) => item.id === over?.id);

    const newOrdering = arrayMove(selectedColumns, oldIndex, newIndex);

    setSelectedColumns(newOrdering);
  };

  const [density, setTableDensity] = useTableDensity();
  const [localDensity, setLocalDensity] = useState<TableDensity>(density);

  return (
    <ModalPanel className="h-full flex flex-col overflow-hidden">
      <div className="mb-6">
        <div className="font-semibold text-base mb-2">Customize Table</div>
        <p className="text-xs text-v2-secondary m-0">
          Need another question answered? Build the table you want.
        </p>
      </div>
      <div className="flex-1 flex overflow-hidden gap-4 mb-6">
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="text-xs font-semibold mb-2">Available Columns</div>
          <div
            className={clsx(
              "border border-v2-tertiary rounded-lg py-4 pl-4 pr-2",
              "flex-1 flex flex-col overflow-hidden",
            )}
          >
            <div className="mb-4">
              <SearchInput
                size="md"
                wrapperClassName="rounded-md"
                className="rounded-md"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                }}
              />
            </div>
            <div className="overflow-auto px-2">
              {groupedMetrics.map((group) => {
                const hasSearchResults =
                  search.length > 0 && group.items.length > 0;

                return (
                  <Accordion
                    key={`${group.group.id}-${accordionKey}`}
                    isExpandedByDefault={
                      hasSearchResults || group.group.id === "northbeam"
                    }
                    title={group.group.label}
                    rightSideContent={(isExpanded) => {
                      return (
                        <div className="flex items-center gap-3">
                          {isExpanded && (
                            <button
                              className="text-2xs text-v2-action underline"
                              onClick={(e) => {
                                e.stopPropagation();

                                const selectedIds = selectedColumns.map(
                                  (e) => e.id,
                                );

                                setSelectedColumns([
                                  ...selectedColumns,
                                  ...group.items
                                    .filter((e) => !selectedIds.includes(e.id))
                                    .map((e) => ({
                                      id: e.id,
                                      label: e.name,
                                    })),
                                ]);
                              }}
                            >
                              Select All
                            </button>
                          )}
                          <Badge variant="information">
                            {
                              group.items.filter((metric) =>
                                selectedColumns
                                  .map((e) => e.id)
                                  .includes(metric.id),
                              ).length
                            }
                          </Badge>
                        </div>
                      );
                    }}
                  >
                    <div className="grid grid-cols-[290px_290px] py-2">
                      {sortBy(group.items, "name").map((metric) => {
                        return (
                          <div key={metric.id} className="py-[1px]">
                            <Checkbox
                              size="sm"
                              label={metric.name}
                              checked={selectedColumns
                                .map((e) => e.id)
                                .includes(metric.id)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedColumns([
                                    ...selectedColumns,
                                    { id: metric.id, label: metric.name },
                                  ]);
                                } else {
                                  setSelectedColumns(
                                    selectedColumns.filter(
                                      (e) => e.id !== metric.id,
                                    ),
                                  );
                                }
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </Accordion>
                );
              })}
            </div>
          </div>
          <div className="mt-4 max-w-[330px]">
            <div className="text-xs font-semibold mb-2">Table Density</div>
            <ButtonGroup
              options={[
                { label: "Compact", value: "compact" },
                { label: "Cozy", value: "cozy" },
                { label: "Comfortable", value: "comfortable" },
              ]}
              onChange={(value) => {
                setLocalDensity(value);
              }}
              value={localDensity}
            />
          </div>
        </div>
        <div className="flex flex-col overflow-hidden w-[200px]">
          <div className="text-xs font-semibold mb-2">Column Order</div>
          <div className="max-h-[568px] overflow-y-auto overflow-x-hidden">
            <div className="rounded bg-v2-highlight">
              <MetricItem
                className="bg-transparent"
                disabled
                sortable={false}
                id="name"
                label="Name"
                checked={true}
              />
              <MetricItem
                className="bg-transparent hover:bg-v2-highlight"
                sortable={false}
                id="dimension:status"
                label="Status"
                checked={selectedColumns
                  .map((e) => e.id)
                  .includes("dimension:status")}
                onChange={(val) => {
                  if (val) {
                    setSelectedColumns([
                      { id: "dimension:status", label: "Status" },
                      ...selectedColumns,
                    ]);
                  } else {
                    setSelectedColumns(
                      selectedColumns.filter(
                        (e) => e.id !== "dimension:status",
                      ),
                    );
                  }
                }}
              />
            </div>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToParentElement, restrictToVerticalAxis]}
            >
              <SortableContext
                items={selectedColumns
                  .filter((e) => !e.id.startsWith("dimension:"))
                  .map((e) => e.id)}
                strategy={verticalListSortingStrategy}
              >
                {selectedColumns
                  .filter((e) => !e.id.startsWith("dimension:"))
                  .map((col) => (
                    <MetricItem
                      key={col.id}
                      id={col.id}
                      label={col.label}
                      checked={true}
                      onChange={() => {
                        setSelectedColumns(
                          selectedColumns.filter((e) => e.id !== col.id),
                        );
                      }}
                    />
                  ))}
              </SortableContext>
            </DndContext>
          </div>
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <TertiaryButton onClick={onClose}>CANCEL</TertiaryButton>
        <PrimaryButton
          onClick={() => {
            prettyLogEventPage({
              domain: "Sales Attribution",
              action: "onClick",
              additionalActionInfo: "Save Customize Table Modal",
              data: {
                pageContext: {
                  selectedMetrics: selectedColumns.map((e) => e.id),
                },
              },
            });
            setTableDensity(localDensity);
            onChange(selectedColumns.map((e) => e.id));
            onClose();
          }}
        >
          SAVE
        </PrimaryButton>
      </div>
    </ModalPanel>
  );
};
